import React from 'react';
import { useSelector } from 'react-redux';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';

// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';

// Import modules from this directory
import css from './CustomExtendedDataField.module.css';
import { fieldDescriptions } from '../../util/enums';
import { getEffectiveLocale } from '../../util/genericHelpers';
import { FIELD_HEADING_TH, OPTION_LABEL_TH } from '../../util/thaiFields';

/** ✅ Localize field label by fieldConfig.key */
const getLocalizedFieldLabel = (fieldConfig, selectedLanguage) => {
  const baseLabel = fieldConfig?.saveConfig?.label || fieldConfig?.label;
  if (selectedLanguage !== 'th') return baseLabel;
  const key = fieldConfig?.key;
  return (key && FIELD_HEADING_TH[key]) || baseLabel;
};

const createFilterOptions = options =>
  options
    .slice()
    .sort((a, b) => {
      if (a.label === 'Other') return 1;
      if (b.label === 'Other') return -1;
      return a.label.localeCompare(b.label);
    })
    .map(o => ({ key: `${o.option}`, label: o.label }));

/** ✅ Match enum option label keys */
const getThaiLabel = (option, optionLabelTh) => {
  if (!option) return null;

  if (optionLabelTh[option]) return optionLabelTh[option];

  const accessibilityKey = `amenity_accessibility_${option}`;
  if (optionLabelTh[accessibilityKey]) return optionLabelTh[accessibilityKey];

  const amenityKey = `amenity_${option}`;
  if (optionLabelTh[amenityKey]) return optionLabelTh[amenityKey];

  if (option === 'street_level_entry' && optionLabelTh.amenity_street_access) {
    return optionLabelTh.amenity_street_access;
  }

  return null;
};

const localizeEnumOptions = (enumOptions = [], selectedLanguage, optionLabelTh = {}) => {
  if (selectedLanguage !== 'th') return enumOptions;

  return enumOptions.map(o => {
    const thaiLabel = getThaiLabel(o.option, optionLabelTh);
    return { ...o, label: thaiLabel || o.label };
  });
};

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, selectedLanguage } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};

  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });

  const filterOptions = createFilterOptions(enumOptions);
  const label = getLocalizedFieldLabel(fieldConfig, selectedLanguage);

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => (
        <option key={optionConfig.key} value={optionConfig.key}>
          {optionConfig.label}
        </option>
      ))}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, selectedLanguage } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};

  const intl = useIntl();

  const label = getLocalizedFieldLabel(fieldConfig, selectedLanguage);

  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};
  const fieldKey = fieldConfig?.saveConfig?.label || fieldConfig?.label;

  const fieldDescriptionId = fieldDescriptions?.find(item => item.key === fieldKey)?.descriptionId;

  const fieldDescription = fieldDescriptionId ? intl.formatMessage({ id: fieldDescriptionId }) : '';

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={
        <>
          <span>{label}</span>
          {fieldDescription ? (
            <>
              <br />
              <br />
              <span className={css.customLabel}>{fieldDescription}</span>
            </>
          ) : null}
        </>
      }
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, selectedLanguage } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};

  const label = getLocalizedFieldLabel(fieldConfig, selectedLanguage);

  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="textarea"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, selectedLanguage } = props;
  const { minimum, maximum, saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};

  const label = getLocalizedFieldLabel(fieldConfig, selectedLanguage);

  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });

  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        if (e.target === document.activeElement) {
          e.target.blur();
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, selectedLanguage } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};

  const label = getLocalizedFieldLabel(fieldConfig, selectedLanguage);

  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, selectedLanguage } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};

  const label = getLocalizedFieldLabel(fieldConfig, selectedLanguage);

  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { currentUser } = useSelector(state => state.user);
  const selectedLanguage = getEffectiveLocale(currentUser);

  const { enumOptions = [], schemaType } = props?.fieldConfig || {};

  // ✅ localize enum option labels
  const localizedEnumOptions = localizeEnumOptions(enumOptions, selectedLanguage, OPTION_LABEL_TH);

  // ✅ pass localized enumOptions + selectedLanguage to ALL field components
  const nextProps = {
    ...props,
    fieldConfig: {
      ...props.fieldConfig,
      enumOptions: localizedEnumOptions,
    },
    selectedLanguage,
  };

  const renderFieldComponent = (FieldComponent, p) => (
    <FieldComponent {...p} intl={intl} selectedLanguage={selectedLanguage} />
  );

  return schemaType === SCHEMA_TYPE_ENUM && localizedEnumOptions
    ? renderFieldComponent(CustomFieldEnum, nextProps)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && localizedEnumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, nextProps)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, nextProps)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, nextProps)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, nextProps)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, nextProps)
    : null;
};

export default CustomExtendedDataField;
